/**
 * clipboard.ts — SSOT cross-platform clipboard utility.
 *
 * Handles both:
 *   - Web: navigator.clipboard.writeText (async, secure context)
 *   - React Native: Clipboard.setString (sync, always available)
 *
 * Usage:
 *   import { copyToClipboard } from '@share/shared';
 *   copyToClipboard('https://...');
 */

/**
 * Copies `text` to the system clipboard.
 * Works on both web (navigator.clipboard) and React Native (Clipboard).
 */
export function copyToClipboard(text: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    // Web — async, but we intentionally don't await here to keep callsites simple
    navigator.clipboard.writeText(text).catch(() => {
      // Fallback: create a temporary textarea and execCommand
      _execCommandFallback(text);
    });
  } else {
    // React Native
    // Dynamic require so web builds don't pull in the native module
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { Clipboard } = require('react-native');
      Clipboard.setString(text);
    } catch {
      _execCommandFallback(text);
    }
  }
}

/**
 * Reads text from the system clipboard.
 * Works on both web (navigator.clipboard) and React Native (Clipboard).
 */
export async function readFromClipboard(): Promise<string> {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.readText) {
    try {
      return (await navigator.clipboard.readText()) || '';
    } catch {
      return '';
    }
  } else {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { Clipboard } = require('react-native');
      if (Clipboard && Clipboard.getString) {
        return (await Clipboard.getString()) || '';
      }
      return '';
    } catch {
      return '';
    }
  }
}

/** Legacy textarea execCommand fallback for browsers without clipboard API. */
function _execCommandFallback(text: string): void {
  if (typeof document === 'undefined') return;
  const el = document.createElement('textarea');
  el.value = text;
  el.style.position = 'fixed';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.focus();
  el.select();
  try { document.execCommand('copy'); } catch { /* ignore */ }
  document.body.removeChild(el);
}
